// Student + Parent — exact Figma content
// Frames: 18 Overview, 19 Experiences, 20 Learning Hub & Progress, 21 How It Works, 22 Family Access, 23 Safety & FAQs, 24 Access Request

export const SP_NAV = [
  { label: 'Overview',    to: '/student-parent' },
  { label: 'Experiences', to: '/student-parent/experiences' },
  { label: 'Learning Hub',to: '/student-parent/learning-hub' },
  { label: 'How It Works',to: '/student-parent/how-it-works' },
  { label: 'Progress',    to: '/student-parent/learning-hub#progress' },
  { label: 'Access',      to: '/student-parent/access' },
];

export const SP_CTA = {
  primary:   { label: 'Request Access', to: '/access-request' },
  secondary: { label: 'Login',          to: '#login' },
};

export const SP_FOOTER = {
  brandNote: 'Conceptualized, designed and developed by Dr. Keyur Sorathia & team at the Embedded Interaction Lab, Department of Design, IIT Guwahati.',
  cols: [
    { title: 'Student & Parent', items: [
      { label: 'Overview',                to: '/student-parent' },
      { label: 'Learning Experiences',    to: '/student-parent/experiences' },
      { label: 'Learning Hub & Progress', to: '/student-parent/learning-hub' },
      { label: 'Family Access',           to: '/student-parent/access' },
    ]},
    { title: 'Support', items: [
      { label: 'Access & Setup',   to: '/student-parent/access' },
      { label: 'Safety & Comfort', to: '/student-parent/faqs' },
      { label: 'FAQs',             to: '/student-parent/faqs' },
      { label: 'Request Access',   to: '/access-request' },
    ]},
    { title: 'Login', items: [
      { label: 'Student portal',   to: '#student-portal' },
      { label: 'Parent dashboard', to: '#parent-dashboard' },
      { label: 'Family access',    to: '#family-access' },
    ]},
  ],
};

// Subject color map — used on module cards
export const SUBJECT_COLORS = {
  'Science':             { gradient: 'from-purple-500 via-royal to-cyan',     dot: 'bg-purple' },
  'Mathematics':         { gradient: 'from-royal via-cyan to-mint',           dot: 'bg-royal' },
  'History':             { gradient: 'from-amber-500 via-purple to-royal',    dot: 'bg-amber-500' },
  'Nature':              { gradient: 'from-mint via-cyan to-royal',           dot: 'bg-mint' },
  'Space':               { gradient: 'from-navy via-royal to-cyan',           dot: 'bg-navy' },
  'Art':                 { gradient: 'from-purple via-royal to-mint',         dot: 'bg-purple' },
  'Environment':         { gradient: 'from-mint via-royal to-purple',         dot: 'bg-mint' },
  'Science exploration': { gradient: 'from-amber-500 via-royal to-purple',    dot: 'bg-amber-500' },
  'Human experience':    { gradient: 'from-cyan via-royal to-purple',         dot: 'bg-cyan' },
  'Career exploration':  { gradient: 'from-royal via-purple to-mint',         dot: 'bg-royal' },
};

export const STATUS_STYLES = {
  'Available':      { bg: 'bg-mint-50',   text: 'text-mint-600',   ring: 'ring-mint/20' },
  'In development': { bg: 'bg-amber-50',  text: 'text-amber-700',  ring: 'ring-amber-200' },
  'Upcoming':       { bg: 'bg-canvas-200',text: 'text-ink-600',    ring: 'ring-canvas-200' },
};

// 18 — OVERVIEW
export const SP_OVERVIEW = {
  hero: {
    eyebrows: ['FOR STUDENTS', 'FOR PARENTS'],
    title1: 'Understand difficult concepts.',
    title2: 'Explore beyond the textbook.',
    desc:  'Curriculum-aligned VR learning, unlimited practice and visual resources for Grade 8 and 9 Science and Mathematics.',
    pill:  'No software licence fee through June 2027',
    imageEyebrow: '',
  },
  strip: [
    { k: 'GRADES',        v: '8 and 9' },
    { k: 'SUBJECTS',      v: 'Science + Mathematics' },
    { k: 'LANGUAGES',     v: 'English · Hindi · Assamese' },
    { k: 'CORE USE',      v: 'Offline-first' },
    { k: 'DEVELOPED BY',  v: 'Dr. Keyur Sorathia & team at the Embedded Interaction Lab, Department of Design, IIT Guwahati' },
  ],
  perspectives: {
    eyebrow: 'ONE EXPERIENCE, TWO PERSPECTIVES',
    title:   'Built for students and parents',
    desc:    'The same learning ecosystem supports independent study and meaningful parent visibility.',
    students: {
      title: 'For students',
      desc:  'Learn by doing, practise until concepts are clear, ask Gyan Mitra and see your own progress.',
      items: [
        'Explore VR modules',
        'Take unlimited quizzes',
        'Use videos and visual resources',
        'Track completed chapters',
      ],
    },
    parents: {
      title: 'For parents',
      desc:  'Understand how your child is using Gyan Dhara and where additional support may help.',
      items: [
        'View time spent and activity',
        'See modules started and completed',
        'Review conceptual, applied and theoretical scores',
        'Follow recent learning progress',
      ],
    },
  },
  waysToLearn: {
    eyebrow: 'CONNECTED LEARNING',
    title:   'Three ways to learn',
    desc:    'VR, practice and progress work together instead of functioning as separate products.',
    items: [
      { icon: 'Headset',        title: 'Learn in VR',                    desc: 'Interact with difficult concepts through interactive, experiential, guided, self-paced experiences.',       color: 'purple' },
      { icon: 'ClipboardCheck', title: 'Practice with Web Learning Hub',  desc: 'Take unlimited conceptual, theoretical and applied quizzes.',                    color: 'royal' },
      { icon: 'BarChart3',      title: 'Analyze with Web Learning Hub',   desc: 'Review completion, time spent and performance by chapter.',                       color: 'mint' },
    ],
  },
  curriculumHeader: {
    eyebrow: 'CURRICULUM LEARNING',
    title:   'Grade 8 and 9 Science and Mathematics',
    desc:    'Available modules are aligned with NCERT concepts and designed for individual 35–45 minute learning sessions.',
  },
  curriculumHome: [
    { subject: 'Science',     status: 'Available',      title: 'Sound' },
    { subject: 'Science',     status: 'Available',      title: 'Combustion & Flame' },
    { subject: 'Science',     status: 'Available',      title: 'Friction' },
    { subject: 'Mathematics', status: 'Available',      title: 'Perimeter' },
    { subject: 'Mathematics', status: 'Available',      title: 'Area 2D' },
    { subject: 'Mathematics', status: 'Available',      title: 'Area 3D' },
    { subject: 'Science',     status: 'Available',      title: 'Volume' },
    { subject: 'Science',     status: 'In development', title: 'Work' },
  ],
  explorationHeader: {
    eyebrow: 'EXPLORE BEYOND TEXTBOOKS',
    title:   'Immersive journeys for curiosity and discovery',
    desc:    'Explore space, history, art, living worlds and human experiences beyond the core curriculum.',
  },
  explorationHome: [
    { subject: 'History',              status: 'Available', title: 'Taj Mahal' },
    { subject: 'History',              status: 'Available', title: 'Qutub Minar' },
    { subject: 'History',              status: 'Available', title: 'India Gate' },
    { subject: 'Science exploration',  status: 'Available', title: 'Age of Dinosaurs' },
    { subject: 'Nature',               status: 'Available', title: 'Living World Safari' },
    { subject: 'Space',                status: 'Available', title: 'Experience Space Station' },
  ],
  learningHubTeaser: {
    eyebrow: 'WEB LEARNING HUB',
    title:   'Continue learning beyond VR',
    desc:    'Use chapter videos, visual resources, quizzes and Gyan Mitra through the learner website.',
    items: [
      { icon: 'Video',         title: 'Chapter videos',    desc: 'Revisit difficult ideas through short visual explanations.', color: 'purple' },
      { icon: 'FileText',      title: 'Visual resources',  desc: 'Use images, visual PDFs and teacher-shared materials.',       color: 'royal' },
      { icon: 'ClipboardList', title: 'Unlimited quizzes', desc: 'Practise conceptual, theoretical and applied understanding.', color: 'cyan' },
      { icon: 'MessageSquare', title: 'Gyan Mitra',        desc: 'Ask voice and visual questions in supported languages.',     color: 'mint' },
    ],
    dashboard: {
      title: 'Student web learning hub',
      desc:  'Continue a chapter, take a quiz or review recent learning.',
      rows: [
        { k: 'CONTINUE LEARNING', v: 'Sound · 65%' },
        { k: 'QUIZ PRACTICE',     v: '3 learning types' },
        { k: 'RECENT PROGRESS',   v: '4 modules completed' },
      ],
      cta: 'Open Learner Login',
    },
  },
  journey: {
    eyebrow: 'INDIVIDUAL JOURNEY',
    title:   'From curiosity to progress',
    desc:    'A simple learning flow that students can follow independently.',
    steps: [
      { n: 1, title: 'Choose a module',         desc: 'Select by grade, subject or interest.' },
      { n: 2, title: 'Five-minute orientation',  desc: 'Learn headset use and navigation.' },
      { n: 3, title: 'Complete the VR session',   desc: 'Explore individually for 35–45 minutes.' },
      { n: 4, title: 'Practise with quizzes',     desc: 'Answer conceptual, applied and theoretical questions.' },
      { n: 5, title: 'Use learning resources',    desc: 'Watch videos and review visual material.' },
      { n: 6, title: 'Review progress',           desc: 'See strengths and areas to revisit.' },
    ],
  },
  progress: {
    eyebrow: 'PROGRESS',
    title:   'Visible learning for students and parents',
    desc:    'Individual accounts connect learning activity, chapter completion and assessment performance.',
    overviewTitle: 'Learning overview',
    metrics: [
      { k: '6h 25m', label: 'TIME SPENT' },
      { k: '8 / 12', label: 'MODULES COMPLETED' },
      { k: '78%',    label: 'AVERAGE QUIZ SCORE' },
    ],
    performance: [
      { label: 'Conceptual',  v: 82 },
      { label: 'Applied',     v: 74 },
      { label: 'Theoretical', v: 78 },
    ],
    parentRows: [
      'Recent activity and time spent',
      'Modules started and completed',
      'Quiz scores by learning type',
      'Learning outcomes covered',
      'Topics that may need review',
    ],
    note: 'Individual VR analytics require a student account.',
  },
  comfort: {
    eyebrow: 'COMFORT AND RESPONSIBLE USE',
    title:   'Designed for focused, age-appropriate sessions',
    desc:    'Gyan Dhara is preferably used by learners aged 12 and above.',
    items: [
      { badge: '12+', title: 'Age 12+ preferred',    desc: 'Age-appropriate use and adult guidance.' },
      { badge: '35',  title: '35–45 minute modules', desc: 'One focused learning session at a time.' },
      { badge: '1:1', title: 'One learner per headset', desc: 'Individual interaction and personal space.' },
      { badge: '5m',  title: 'Five-minute orientation', desc: 'Basic navigation before the first module.' },
    ],
  },
  access: {
    title: 'Complete software access through June 2027',
    included: 'No software licence fee is charged for the Gyan Dhara VR ecosystem, learner website, quizzes, dashboards, updates and newly released learning modules during the access period.',
    separate: 'Hardware, accessories, physical setup, device management, configuration, courier and optional support are separately funded.',
  },
  familyTeaser: {
    eyebrow: 'FAMILY ACCESS',
    title:   'Learn with an individual family setup',
    desc:    'The complete Gyan Dhara learning ecosystem can be configured for use by a student or family at home.',
    card: {
      icon: 'Home',
      title: 'Individual family access',
      desc:  'Use one configured Meta Quest 3S at home. A parent or guardian supports charging, safe setup and learner access.',
      items: [
        'One learner per headset',
        'Individual account recommended for personal analytics',
        'Hardware, setup and courier are separate from software access',
      ],
    },
  },
  cta: {
    eyebrow: 'START LEARNING',
    kicker:  'Choose your next step',
    desc:    'Explore the available modules or request access for your child or family.',
    title:   'Turn difficult concepts into experiences you can understand.',
    subtitle:'Learn in VR, continue with resources and quizzes, and make progress visible.',
  },
};

// 19 — LEARNING EXPERIENCES
export const SP_EXPERIENCES = {
  hero: {
    eyebrow: 'LEARNING EXPERIENCES',
    title:   'Choose what you want to understand or explore',
    desc:    'Browse NCERT-aligned curriculum modules and exploratory journeys across Science, Mathematics, space, history, art and nature.',
    pill:    'CURRICULUM + EXPLORATION',
  },
  curriculum: {
    eyebrow: 'CURRICULUM LEARNING',
    title:   'Available now',
    desc:    'Each module includes learning outcomes, a 35–45 minute VR experience, multilingual support and connected quiz practice.',
    items: [
      { subject: 'Science',     status: 'Available', title: 'Sound',                desc: 'Vibration, amplitude and pitch' },
      { subject: 'Science',     status: 'Available', title: 'Combustion & Flame',    desc: 'Materials and combustion' },
      { subject: 'Science',     status: 'Available', title: 'Friction',              desc: 'Forces in everyday life' },
      { subject: 'Science',     status: 'Available', title: 'Zero Gravity',           desc: 'Gravity and motion' },
      { subject: 'Science',     status: 'Available', title: "Gravity's Grip",         desc: 'Gravitational force' },
      { subject: 'Science',     status: 'Available', title: 'Human Excretory System', desc: 'Body systems' },
      { subject: 'Science',     status: 'Available', title: 'Falltopia',              desc: 'Falling and motion' },
      { subject: 'Science',     status: 'Available', title: 'AeroQuest',              desc: 'Air and flight' },
      { subject: 'Science',     status: 'Available', title: 'Work',                    desc: 'Work in physics' },
      { subject: 'Science',     status: 'Available', title: 'Energy',                  desc: 'Energy and its forms' },
      { subject: 'Mathematics', status: 'Available', title: 'Perimeter',              desc: 'Boundary and measurement' },
      { subject: 'Mathematics', status: 'Available', title: 'Area 2D',                desc: 'Area of two-dimensional shapes' },
      { subject: 'Mathematics', status: 'Available', title: 'Area 3D',                desc: 'Surface area and form' },
      { subject: 'Mathematics', status: 'Available', title: 'Volume',                 desc: 'Cube, cuboid and cylinder' },
    ],
  },
  pipeline: {
    eyebrow: 'CURRICULUM PIPELINE',
    title:   'In development and upcoming',
    desc:    'New modules and software updates are planned in regular release cycles.',
    items: [
      { subject: 'Science',     status: 'In development', title: 'Contact & Non-contact Forces', desc: 'Types of forces' },
      { subject: 'Science',     status: 'In development', title: 'Structure of Atom',         desc: 'Atomic structure' },
      { subject: 'Science',     status: 'In development', title: 'Light',                     desc: 'Reflection and refraction' },
      { subject: 'Mathematics', status: 'In development', title: 'Area of Cone',              desc: 'Curved and total area' },
      { subject: 'Mathematics', status: 'In development', title: 'Area of Sphere',            desc: 'Surface area of sphere' },
      { subject: 'Mathematics', status: 'Upcoming',       title: 'Volume of Sphere and Cone', desc: '' },
    ],
  },
  exploration: {
    eyebrow: 'EXPLORATION AND GYAN VATIKA',
    title:   'Beyond curriculum learning',
    desc:    'These experiences support curiosity, learning history, culture, art and interdisciplinary exploration.',
    items: [
      { subject: 'History',              status: 'Available', title: 'Taj Mahal',              desc: 'Architecture and cultural journey' },
      { subject: 'History',              status: 'Available', title: 'Qutub Minar',            desc: 'Exploration and learning journey' },
      { subject: 'History',              status: 'Available', title: 'India Gate',              desc: 'Exploration and learning journey' },
      { subject: 'Science exploration',  status: 'Available', title: 'Age of Dinosaurs',       desc: 'Prehistoric world' },
      { subject: 'Nature',               status: 'Available', title: 'Lost World Expedition',   desc: 'Extinct worlds' },
      { subject: 'Nature',               status: 'Available', title: 'Living World Safari',    desc: 'Living animals' },
      { subject: 'Space',                status: 'Available', title: 'Experience Space Station', desc: 'Life in orbit' },
      { subject: 'Art',                  status: 'Available', title: '3D Painting',            desc: 'Create in three dimensions' },
      { subject: 'History',              status: 'Available', title: 'Anne Frank Experience',   desc: 'Historical perspective' },
      { subject: 'Human experience',     status: 'Available', title: 'Notes on Blindness',      desc: 'Perspective and accessibility' },
      { subject: 'Environment',          status: 'Available', title: 'Pollinator Park',         desc: 'Future of pollination' },
    ],
  },
};

// 20 — LEARNING HUB & PROGRESS
export const SP_LEARNING_HUB = {
  hero: {
    eyebrow: 'LEARN BEYOND VR',
    title:   'Everything you need to continue learning',
    desc:    'The Gyan Dhara Web Learning Hub connects chapter resources, unlimited AI-driven quizzes, progress and analytics with the VR experience.',
    dashboard: {
      k: 'MY LEARNING',
      v: 'Continue Sound',
      pk: 'Module progress',
      pv: '68%',
    },
  },
  connected: {
    eyebrow: 'WEB LEARNING HUB',
    title:   'Three connected tools',
    desc:    'Move between understanding, practice and progress without losing chapter context.',
    items: [
      { letter: '▶',  color: 'purple', title: 'Chapter videos',           desc: 'Short visual explanations connected to the chapter and learning outcomes.' },
      { letter: 'PDF',color: 'royal',  title: 'Visual learning resources', desc: 'Magical and easy to learn infographics, visual PDFs and materials for engaging learning.' },
      { letter: 'Q',  color: 'cyan',   title: 'Unlimited quizzes',        desc: 'Conceptual, theoretical and applied questions generated for repeated practice.' },
    ],
  },
  practice: {
    eyebrow: 'AI-DRIVEN UNLIMITED QUIZZES',
    title:   'AI-driven Unlimited Quizzes',
    desc:    'The learner can practise the same chapter with unlimited AI-based quizzes in different ways rather than repeating same questions.',
    types: [
      { letter: 'C', color: 'purple', title: 'Conceptual learning',  desc: 'Relationships, causes, comparisons and mental-model clarity.' },
      { letter: 'T', color: 'royal',  title: 'Theoretical learning', desc: 'Terms, definitions, chapter knowledge and formula recall.' },
      { letter: 'A', color: 'mint',   title: 'Applied learning',     desc: 'Use knowledge in real-world and unfamiliar situations.' },
    ],
    demo: {
      title: 'Sound · Applied practice',
      question: 'A student notices that a guitar string sounds louder after it is plucked more strongly. Which change best explains the louder sound?',
      options: [
        { k: 'A', v: 'The frequency increases' },
        { k: 'B', v: 'The amplitude increases', correct: true },
        { k: 'C', v: 'The medium changes' },
        { k: 'D', v: 'The pitch decreases' },
      ],
      afterTitle: 'After answering',
      afterLines: ['See the correct answer, explanation and why the other options are less appropriate.','Practice again with a new question.'],
    },
  },
  mitra: {
    eyebrow: 'GYAN MITRA',
    title:   'Ask when a concept is unclear',
    desc:    'Gyan Mitra provides voice and visual explanations within the supported learning context.',
    chat: {
      q: 'Why does a larger vibration make a louder sound?',
      a: 'A larger vibration has greater amplitude. This transfers more energy to the surrounding medium, so the sound is perceived as louder.',
    },
    languages: ['English','Hindi','Assamese'],
    note: 'Internet is required for Gyan Mitra.',
  },
  personal: {
    eyebrow: 'LEARNING ACTIVITY AND PROGRESS',
    title:   'Understand what to continue, complete and revisit',
    desc:    'The Learning Hub combines study resources with progress views, so the next learning action is always clear.',
    overviewTitle: 'Learning overview',
    metrics: [
      { k: '6h 25m', label: 'TIME SPENT' },
      { k: '8 / 12', label: 'MODULES COMPLETED' },
      { k: '78%',    label: 'AVERAGE QUIZ SCORE' },
    ],
    performance: [
      { label: 'Conceptual', v: 82 },
      { label: 'Applied',    v: 74 },
      { label: 'Theoretical',v: 78 },
    ],
    recent: [
      { title: 'Sound',   status: 'Completed' },
      { title: 'Area 2D', status: 'Quiz practice' },
      { title: 'Friction',status: 'In progress' },
    ],
    parent: {
      title: 'Parent-ready summary',
      desc:  'A simplified view of the learner\u2019s recent activity and understanding.',
      rows: [
        { k: 'Learning this week', v: '2h 35m' },
        { k: 'Modules completed',  v: '3' },
        { k: 'Quiz attempts',      v: '11' },
        { k: 'Strongest area',     v: 'Conceptual' },
      ],
      note: 'Parents can also review chapter time, covered outcomes and topics that may need attention.',
    },
    account: {
      title: 'One account connects the complete learning journey',
      desc:  'An individual account links VR activity, chapter resources, quizzes and parent summaries without creating a separate progress experience.',
      steps: [
        { n:1, title:'Continue learning',   desc:'Resume the current chapter or module.' },
        { n:2, title:'See what is complete', desc:'Review modules, chapters and quizzes.' },
        { n:3, title:'Know what to revisit',  desc:'Identify weaker areas by learning type.' },
        { n:4, title:'Share progress',        desc:'Parents view simplified summaries.' },
      ],
      note: 'Personal VR analytics require an individual learner account.',
    },
  },
  cta: {
    title: 'Continue learning outside the headset.',
    desc:  'Open the learner portal to continue learning, practise and review progress.',
    button:'Open Learner Login',
  },
};

// 21 — HOW LEARNING WORKS
export const SP_HOW = {
  hero: {
    eyebrow: 'SIMPLE LEARNING FLOW',
    title:   'From choosing a chapter to understanding progress',
    desc:    'Gyan Dhara combines a focused VR session with continued practice and clear progress views.',
    badge:   'VR  \u2192  QUIZ  \u2192  PROGRESS',
  },
  journey: {
    eyebrow: 'STUDENT JOURNEY',
    title:   'Six steps from selection to reflection',
    desc:    'The process is designed for independent learning after a short orientation.',
    steps: [
      { n:1, title:'Choose',     desc:'Select by chapter, subject or curiosity.' },
      { n:2, title:'Orient',     desc:'Complete a five-minute headset orientation.' },
      { n:3, title:'Experience', desc:'Learn individually in VR for 35\u201345 minutes.' },
      { n:4, title:'Practice',   desc:'Take conceptual, theoretical and applied quizzes.' },
      { n:5, title:'Continue',   desc:'Use videos, visual PDFs and Gyan Mitra.' },
      { n:6, title:'Review',     desc:'See progress and areas to revisit.' },
    ],
  },
  connectivity: {
    eyebrow: 'CONNECTIVITY',
    title:   'Offline-first with optional connected services',
    desc:    'Core curriculum use should not depend on continuous internet availability.',
    offline: {
      badge: 'WORKS OFFLINE',
      title: 'Core learning',
      items: ['Installed VR modules', 'Self-guided curriculum sessions', 'Basic home learning operation'],
    },
    online: {
      badge: 'INTERNET REQUIRED',
      title: 'Connected services',
      items: ['Gyan Mitra voice and visual Q&A', 'Learning-data synchronisation', 'Remote configuration and some updates'],
    },
  },
  account: {
    eyebrow: 'INDIVIDUAL ACCOUNTS',
    title:   'Your complete learning platform',
    desc:    'An individual account links VR activity with chapters, quizzes and progress views.',
    withAcc: {
      title: 'With an individual account',
      items: ['VR module completion', 'Time spent', 'Chapter activity', 'Quiz scores', 'Conceptual / Applied / Theoretical breakdown'],
    },
    note: 'Without an individual account, the VR experience can still be used, but personal VR analytics are not available.',
  },
  family: {
    eyebrow: 'FAMILY ACCESS',
    title:   'Designed for individual use at home',
    desc:    'The student or family uses one configured Meta Quest 3S with parent-supported setup, charging and account access.',
    subEyebrow: 'INDIVIDUAL FAMILY SETUP',
    subTitle: 'One learner, one configured headset, one connected learning journey',
    subDesc:  'A parent or guardian supports safe setup, charging and account access. The Gyan Dhara team prepares the software, learner account and updates.',
    subItems: [
      'One learner per headset',
      'Individual account recommended for progress',
      'Core learning works offline',
    ],
  },
  responsibilities: {
    eyebrow: 'RESPONSIBILITIES',
    title:   'Who does what',
    desc:    'Clear roles keep the family learning experience simple and reliable.',
    items: [
      { letter: 'S',  color: 'purple', title: 'Student',           desc: 'Uses one headset, completes modules and practises through the learner website.' },
      { letter: 'P',  color: 'royal',  title: 'Parent or guardian', desc: 'Supports safe use, charging and reviews simplified progress summaries.' },
      { letter: 'GD', color: 'mint',   title: 'Gyan Dhara team',    desc: 'Configures software, learner accounts, updates and optional remote support.' },
    ],
  },
};

// 22 — FAMILY ACCESS
export const SP_ACCESS = {
  hero: {
    eyebrow: 'ACCESS AND SETUP',
    title:   'Set up Gyan Dhara for your child',
    desc:    'The complete learning ecosystem can be configured for individual use by a student or family at home.',
    pill:    'FAMILY ACCESS',
  },
  software: {
    eyebrow: 'SOFTWARE ACCESS',
    title:   'Included through June 2027',
    desc:    'Gyan Dhara\u2019s educational software bundle is provided without a licence fee during the access period.',
    included: {
      title: 'Included without a software licence fee',
      items: [
        'All available curriculum VR modules',
        'Exploratory experiences',
        'New modules and software updates',
        'Student, teacher and parent websites',
        'Unlimited conceptual, theoretical and applied quizzes',
        'Chapter videos and visual resources',
        'Gyan Mitra',
        'Learning analytics',
      ],
    },
    separate: {
      title: 'Separately funded by the family',
      items: [
        'Meta Quest 3S hardware',
        'Comfort straps and other accessories',
        'Rotating stools',
        'Device-management and initial configuration',
        'Packaging, courier and logistics',
        'TV or computer for casting, when required',
        'Optional training, technical support and maintenance',
        'Repair and replacement',
      ],
    },
  },
  connectivity: {
    eyebrow: 'CONNECTIVITY',
    title:   'Offline-first operation',
    desc:    'Core learning works offline. Internet enables Gyan Mitra, synchronisation and remote services.',
    items: [
      { badge: 'OFF', title: 'Core learning',      desc: 'Installed VR modules and self-guided sessions work offline.' },
      { badge: 'ON',  title: 'Connected services', desc: 'Gyan Mitra, data synchronisation and remote device support need internet.' },
      { badge: 'UP',  title: 'Updates',            desc: 'Software and new modules are prepared by the Gyan Dhara team.' },
    ],
  },
  steps: {
    eyebrow: 'GETTING STARTED',
    title:   'Four steps to access',
    desc:    'No software selection process is used; the discussion confirms readiness and the appropriate setup.',
    items: [
      { n:1, title:'Submit interest',       desc:'Tell us about the learner and preferred home setup.' },
      { n:2, title:'Discuss requirements',   desc:'Confirm learner numbers, hardware and configuration needs.' },
      { n:3, title:'Confirm and configure',  desc:'Fund applicable hardware and logistics; Gyan Dhara prepares the system.' },
      { n:4, title:'Dispatch and begin',      desc:'Receive the configured package and start using the learning ecosystem.' },
    ],
  },
  familyPathway: {
    eyebrow: 'FAMILY SETUP',
    title:   'One clear pathway for individual access',
    desc:    'A family receives a configured learning setup designed for safe, independent use at home.',
    subEyebrow: 'INDIVIDUAL FAMILY ACCESS',
    subTitle:   'A configured Meta Quest 3S for focused learning at home',
    subDesc:    'A parent or guardian supports charging, safe setup and account access. The Gyan Dhara team prepares the learner account, software and updates.',
    subItems: [
      'One learner uses one headset at a time',
      'Comfort strap and rotating stool are recommended',
      'Individual account enables personal analytics',
      'Core learning works offline',
    ],
  },
  cta: {
    title: 'Bring the complete Gyan Dhara learning ecosystem home.',
    desc:  'Request access to discuss the setup that fits your situation.',
  },
};

// 23 — SAFETY & FAQS
export const SP_FAQ = {
  hero: {
    eyebrow: 'SAFETY AND SUPPORT',
    title:   'Comfortable, responsible and informed use',
    desc:    'Clear setup guidance helps students focus on learning rather than the device.',
    pill:    '12+  \u00b7  35\u201345 MIN  \u00b7  SEATED',
  },
  core: {
    eyebrow: 'CORE GUIDANCE',
    title:   'Designed for focused learning sessions',
    desc:    'Gyan Dhara is preferably used by learners aged 12 and above with appropriate adult or parent guidance.',
    items: [
      { badge: '12+', title: 'Age 12+ preferred',       desc: 'Use should match the learner\u2019s comfort and parent or family guidance.' },
      { badge: '5m',  title: 'Short orientation',        desc: 'Complete a five-minute introduction before the first module.' },
      { badge: '1:1', title: 'One learner per headset',  desc: 'Maintain personal space and individual interaction.' },
      { badge: '45m', title: 'Focused module duration',  desc: 'Most curriculum experiences take approximately 35\u201345 minutes.' },
    ],
  },
  checklist: {
    eyebrow: 'SESSION CHECKLIST',
    title:   'Before, during and after use',
    desc:    'A simple routine supports comfort, device care and consistent operation.',
    groups: [
      { n:1, title:'Before the session', items: ['Charge the headset and controllers','Check the comfort strap and lenses','Clear the seated movement area','Confirm the correct learner account'] },
      { n:2, title:'During the session', items: ['Use a stable rotating stool','Keep one learner per headset','Stop and inform an adult if uncomfortable','Follow the guided module sequence'] },
      { n:3, title:'After the session',  items: ['Exit the learner account when required','Clean contact surfaces using the approved protocol','Return devices to charging and storage','Synchronise data when internet is available'] },
    ],
  },
  faqs: {
    eyebrow: 'FREQUENTLY ASKED QUESTIONS',
    title:   'What students and parents usually ask',
    desc:    'Important information about access, devices, internet, progress and software availability.',
    items: [
      { q: 'Do I need internet to use Gyan Dhara?', a: 'Core installed VR modules work offline. Internet is needed for Gyan Mitra, data synchronisation and some remote services.' },
      { q: 'Can a student use Gyan Dhara at home?',  a: 'Yes. A family can request individual access using a configured Meta Quest 3S and parent-supported setup.' },
      { q: 'Is the software free?',                   a: 'The educational software bundle is provided without a licence fee through June 2027. Hardware, setup, courier and optional support are separate.' },
      { q: 'Which headset is supported?',             a: 'The current ecosystem is prepared for Meta Quest 3S.' },
      { q: 'Can parents view progress?',              a: 'Yes. A parent account can show simplified activity, completion and quiz summaries for the child.' },
      { q: 'Why is an individual student account recommended?', a: 'It connects VR module completion and time spent with chapter activity and quiz performance.' },
      { q: 'Which languages are available?',          a: 'English, Hindi and Assamese are currently functional across the supported experience.' },
      { q: 'How long is one module?',                 a: 'A typical curriculum module takes approximately 35\u201345 minutes after the first orientation.' },
    ],
  },
  cta: {
    title: 'Need help deciding whether family access is appropriate?',
    desc:  'Connect with the Gyan Dhara team to discuss the learner, available hardware and preferred use.',
    button:'Request Access Guidance',
  },
};

// 24 — ACCESS REQUEST FORM
export const SP_REQUEST = {
  hero: {
    eyebrow: 'REQUEST ACCESS',
    title:   'Request student or family access',
    desc:    'Tell us how you plan to use Gyan Dhara. The team will respond within 48 hours to discuss access and configuration.',
    softwareCard: {
      title: 'Software access',
      lines: [
        'No licence fee through June 2027',
        'Hardware, accessories, setup, courier and optional support are separately funded.',
      ],
    },
  },
  next: [
    { n:1, title:'Request received', desc:'Immediate confirmation' },
    { n:2, title:'Team response',     desc:'Within 48 hours' },
    { n:3, title:'Setup discussion',  desc:'Family setup requirements' },
    { n:4, title:'Configuration',     desc:'Hardware, account and software' },
    { n:5, title:'Begin learning',    desc:'After setup and dispatch' },
  ],
  consider: [
    { n:1, title:'Learner pathway',    desc:'Learner and family access.' },
    { n:2, title:'Hardware readiness', desc:'Existing Quest 3S or planned purchase.' },
    { n:3, title:'Individual account', desc:'Recommended for personal analytics.' },
    { n:4, title:'Internet availability', desc:'Only required for connected services.' },
  ],
};
